import axios from 'axios';
import {
  OversightOrganization,
  OversightOrganizationType,
} from './OversightUtils';
import { LanguageObject } from '@/i18n';
import fileDownload from 'js-file-download';

interface DataSet<T = any> {
  id: number;
  key: string;
  name: string;
  oversighttype: OversightOrganizationType;
}

interface DataSetContent<T = any> {
  id: number;
  name: LanguageObject;
  dataset: Partial<DataSet>;
  oversights: Partial<OversightOrganization>[];
  content: T;
}

const api = {
  dataset: {
    list: async <T = any>(params?: any) => {
      const resp = await axios.get<DataSet<T>[]>('/dataset/list.php', {
        params: params,
      });
      return resp.data;
    },

    get: async <T = any>(value: number | string) => {
      const resp = await axios.get<DataSet<T>>('/dataset/get.php', {
        params: {
          key: typeof value === 'string' ? value : undefined,
          id: typeof value === 'number' ? value : undefined,
        },
      });
      return resp.data;
    },

    add: async <T = any>(dataset: Partial<DataSet<T>>) => {
      const resp = await axios.post<DataSet<T>>('/dataset/add.php', dataset);
      return resp.data;
    },

    update: async <T = any>(dataset: Partial<DataSet<T>>) => {
      const resp = await axios.post<DataSet<T>>('/dataset/update.php', dataset);
      return resp.data;
    },

    remove: async (value: number | string) => {
      await axios.delete('/dataset/delete.php', {
        params: {
          key: typeof value === 'string' ? value : undefined,
          id: typeof value === 'number' ? value : undefined,
        },
      });
    },
  },

  content: {
    list: async <T = any>(
      datasetkey: string,
      params?: {
        surveyparticipationid?: number;
      }
    ) => {
      const resp = await axios.get<DataSetContent<T>[]>(
        '/datasetData/list.php',
        {
          params: {
            datasetkey: datasetkey,
            ...params,
          },
        }
      );
      return resp.data;
    },

    get: async <T = any>(id: number) => {
      const resp = await axios.get<DataSetContent<T>>('/datasetData/get.php', {
        params: { id: id },
      });
      return resp.data;
    },

    add: async <T = any>(dataset: Partial<DataSetContent<T>>) => {
      const resp = await axios.post<DataSetContent<T>>(
        '/datasetData/add.php',
        dataset
      );
      return resp.data;
    },

    update: async <T = any>(dataset: Partial<DataSetContent<T>>) => {
      const resp = await axios.post<DataSetContent<T>>(
        '/datasetData/update.php',
        dataset
      );
      return resp.data;
    },

    remove: async (id: number) => {
      await axios.delete('/datasetData/delete.php', {
        params: { id: id },
      });
    },

    import: async (
      file: any,
      datasetid: number,
      mode: 'ignoreduplicate' | 'purge'
    ) => {
      const data = new FormData();
      data.set('file', file);
      data.set('mode', mode);
      data.set('datasetid', datasetid?.toString() as string);
      const resp = await axios.post<Document>('/datasetData/import.php', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return resp.data;
    },

    export: async (dataset: Partial<DataSet>) => {
      const resp = await axios.get<any>('/datasetData/export.php', {
        params: { datasetid: dataset.id },
      });
      fileDownload(resp.data, `${dataset.name as string}.csv`);
    },
  },
};

export default {
  api,
};

export { DataSet, DataSetContent };
